import { useQuery } from '@tanstack/react-query';
import {
  fetchFreshness,
  selectFreshness,
  type FetchFreshnessOptions,
  type FreshnessRecord,
  type FreshnessStatus,
} from '@/lib/freshness';

/**
 * Shared query key (replit.md §1.4 — all state flows through the same query layer
 * with shared keys). Every freshness badge in the app reads from this ONE cached
 * query, so the freshness payload is fetched once and selected per entity — a new
 * view gets a badge for free without its own fetch.
 */
export const freshnessQueryKey = ['freshness'] as const;

export interface UseFreshnessResult {
  freshness: FreshnessStatus | undefined;
  record: FreshnessRecord | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useFreshness(entity: string, options?: FetchFreshnessOptions): UseFreshnessResult {
  const query = useQuery({
    queryKey: freshnessQueryKey,
    queryFn: () => fetchFreshness(options),
  });

  const record = query.data ? selectFreshness(query.data, entity) : undefined;
  return {
    freshness: record?.freshness,
    record,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export interface UseFreshnessRecordsResult {
  records: FreshnessRecord[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * The full per-source freshness payload from the SAME shared query — the
 * cash-flow toolbar's per-source badge row reads this so it shows every source's
 * staleness without its own fetch (one query, selected many ways).
 */
export function useFreshnessRecords(options?: FetchFreshnessOptions): UseFreshnessRecordsResult {
  const query = useQuery({
    queryKey: freshnessQueryKey,
    queryFn: () => fetchFreshness(options),
  });
  return {
    records: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
